/**
 * VendorLocationService
 * Service for managing vendor-location associations (composite key)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for vendor location operations with composite key (vendorId + locationId)
 */
export class VendorLocationService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorLocationService.DEFAULT_SERVICE_PATH,
            basePath: VendorLocationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all vendor locations
     * @returns Array of vendor locations
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor locations with full response
     * @returns ResponseData with array of vendor locations
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE ID ====================
    /**
     * Get vendor location by composite key
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @returns The vendor location
     */
    async getById(vendorId, locationId) {
        const response = await this.getByIdRaw(vendorId, locationId);
        if (!response.data) {
            throw new Error(`VendorLocation not found: vendorId=${vendorId}, locationId=${locationId}`);
        }
        return response.data;
    }
    /**
     * Get vendor location by composite key with full response
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @returns ResponseData with vendor location
     */
    getByIdRaw(vendorId, locationId) {
        const url = this.buildUrl(`${vendorId}/${locationId}`);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new vendor location
     * @param data - Vendor location data
     * @returns The created vendor location
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create VendorLocation');
        }
        return response.data;
    }
    /**
     * Create a new vendor location with full response
     * @param data - Vendor location data
     * @returns ResponseData with created vendor location
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update a vendor location
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @param data - Updated vendor location data
     * @returns The updated vendor location
     */
    async update(vendorId, locationId, data) {
        const response = await this.updateRaw(vendorId, locationId, data);
        if (!response.data) {
            throw new Error(`Failed to update VendorLocation: vendorId=${vendorId}, locationId=${locationId}`);
        }
        return response.data;
    }
    /**
     * Update a vendor location with full response
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @param data - Updated vendor location data
     * @returns ResponseData with updated vendor location
     */
    updateRaw(vendorId, locationId, data) {
        const url = this.buildUrl(`${vendorId}/${locationId}`);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a vendor location
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @returns True if deleted successfully
     */
    async delete(vendorId, locationId) {
        const response = await this.deleteRaw(vendorId, locationId);
        return response.success;
    }
    /**
     * Delete a vendor location with full response
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(vendorId, locationId) {
        const url = this.buildUrl(`${vendorId}/${locationId}`);
        return this.http.delete(url);
    }
    // ==================== CUSTOM QUERIES ====================
    /**
     * Get all locations for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor location views
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all locations for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor location views
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
    /**
     * Get all vendors for a specific location
     * @param locationId - Location ID
     * @returns Array of vendor location views
     */
    async getByLocation(locationId) {
        const response = await this.getByLocationRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all vendors for a specific location with full response
     * @param locationId - Location ID
     * @returns ResponseData with array of vendor location views
     */
    getByLocationRaw(locationId) {
        const url = this.buildUrl(`location/${locationId}`);
        return this.http.get(url);
    }
    // ==================== MODEL VIEW QUERIES ====================
    /**
     * Get all vendor locations as model views
     * @returns Array of vendor location model views
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor locations as model views with full response
     * @returns ResponseData with array of vendor location model views
     */
    getViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get vendor location model view by composite key
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @returns The vendor location model view
     */
    async getView(vendorId, locationId) {
        const response = await this.getViewRaw(vendorId, locationId);
        if (!response.data) {
            throw new Error(`VendorLocation view not found: vendorId=${vendorId}, locationId=${locationId}`);
        }
        return response.data;
    }
    /**
     * Get vendor location model view by composite key with full response
     * @param vendorId - Vendor ID
     * @param locationId - Location ID
     * @returns ResponseData with vendor location model view
     */
    getViewRaw(vendorId, locationId) {
        const url = this.buildUrl(`${vendorId}/${locationId}/view`);
        return this.http.get(url);
    }
    /**
     * Get all location model views for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor location model views
     */
    async getViewsByVendor(vendorId) {
        const response = await this.getViewsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all location model views for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor location model views
     */
    getViewsByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}/views`);
        return this.http.get(url);
    }
}
VendorLocationService.DEFAULT_SERVICE_PATH = 'vendors';
VendorLocationService.BASE_PATH = 'api/vendor-locations';
//# sourceMappingURL=VendorLocationService.js.map